import { Activity, ChevronRight } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import { exportVarianceHistory } from '../../lib/excel-export'
import { ExcelDownloadButton } from '../ui/ExcelDownloadButton'
import { COLUMN_IDS, FIELD_KEYS, VARIANCE_COLUMNS } from '../../config/columns'
import { CONFIG } from '../../config/constants'
import { useColumnVisibility } from '../../hooks/useColumnVisibility'
import { useSortedData } from '../../hooks/useSortedData'
import { formatDateToDDMMYYYY, formatISTTime } from '../../lib/date-utils'
import
    {
        formatCount,
        getCountColorClass,
        getShiftColorClass,
        getVarianceColorClass,
        TABLE_CLASSES,
    } from '../../lib/table-utils'
import { type ColumnConfig, ColumnSelector } from '../ColumnSelector'
import { ShiftChip } from '../chips/ShiftChip'
import { VarianceChip } from '../chips/VarianceChip'
import { NumberCell } from '../ui/ghost-table/DataCells'
import { StrikeChip } from '../ui/ghost-table/StrikeChip'
import { TableWrapper } from '../ui/ghost-table/TableWrapper'
import { WidgetErrorBoundary } from '../ui/WidgetErrorBoundary'
import { TableVirtuoso } from 'react-virtuoso'

interface VarianceHistoryTableProps
{
    data: any[]
    isLoading?: boolean
    symbol?: string
    mode?: string
    rowsPerPage?: number
    onRowsPerPageChange?: ( rows: number ) => void
}

// Separate wrapper needs symbol for export, extract from props
const useExportHandler = ( data: any[], symbol?: string ) =>
{
    return useCallback( () =>
    {
        exportVarianceHistory( data || [], symbol || 'Unknown' )
    }, [ data, symbol ] )
}

const VarianceRow = React.memo( ( { row, idx, items, isVisible }: { row: any, idx: number, items: any[], isVisible: ( id: string ) => boolean } ) =>
{
    const tdClass = "p-2 h-[48px] align-middle whitespace-nowrap border-b border-slate-100/30 dark:border-slate-800/30"

    // Extract Data
    const rank = row.rank || row.Rank || 0
    const ceLtp = row.ceLtp ?? row.CE_LTP
    const peLtp = row.peLtp ?? row.PE_LTP

    const maxDiff = row.max_oi_diff ?? row.maxDiff ?? row.variance?.maxDiff ?? 0
    const minDiff = row.min_oi_diff ?? row.minDiff ?? row.variance?.minDiff ?? 0
    const maxStrike = row.max_strike ?? row.maxStrike ?? row.variance?.maxStrike ?? 0
    const minStrike = row.min_strike ?? row.minStrike ?? row.variance?.minStrike ?? 0

    const ceCount = row.ceCount ?? row.ce_count ?? 0
    const peCount = row.peCount ?? row.pe_count ?? 0
    const finalCount = row.totalCount ?? row.total_count ?? ( ceCount + peCount )

    const variance = typeof row.variance === 'number' ? row.variance : ( row.variance?.finalVariance ?? row.finalVariance ?? 0 )
    const shift = row.shift ?? 0
    const shiftOld = row.shiftOld ?? row.shift_old ?? 0
    const atmStrike = row.atm ?? row.atmStrike ?? 0
    const spotPrice = row.spotPrice ?? row.spot_price ?? 0

    // Prev Item for trend/delta
    const prevItem = items[ idx + 1 ]

    return (
        <>
            <td className={ `${tdClass} pl-4 font-mono font-medium text-slate-600 dark:text-slate-400` }>
                { ( () =>
                {
                    let time = '-'
                    let date = ''
                    const val = String( row.timestampIST || row.timestamp || row.TimeStamp || row.created_at || '' )
                    try
                    {
                        time = formatISTTime( val ) || val
                        if ( typeof ( row.timestamp || row.TimeStamp ) === 'number' )
                        {
                            date = formatDateToDDMMYYYY( new Date( row.timestamp || row.TimeStamp ) )
                        } else
                        {
                            if ( val.includes( 'T' ) ) date = val.split( 'T' )[ 0 ] ?? ''
                            else if ( val.includes( ' ' ) ) date = val.split( ' ' )[ 0 ] ?? ''
                        }
                        if ( time.includes( 'T' ) ) time = time.split( 'T' )[ 1 ]?.substring( 0, 5 ) ?? ''
                        if ( time.includes( ' ' ) ) time = time.split( ' ' )[ 1 ]?.substring( 0, 5 ) ?? ''
                    } catch (e) { console.warn('Ignored error in VarianceHistoryTable.tsx:', e); }

                    return (
                        <div className="flex flex-col justify-center">
                            <span className="font-bold text-slate-700 dark:text-slate-200 text-xs">{ time }</span>
                            { date && date !== time && <span className="text-[9px] text-slate-400 font-medium">{ date }</span> }
                        </div>
                    )
                } )() }
            </td>

            { isVisible( COLUMN_IDS.MAX_OI_DIFF ) && (
                <td className={ `${tdClass} text-right` }><NumberCell value={ maxDiff } decimals={ 2 } suffix="%" className="text-rose-600 font-bold" /></td>
            ) }

            { isVisible( COLUMN_IDS.MAX_STRIKE ) && (
                <td className={ `${tdClass} text-center shadow-[inset_-1px_0_0_0_rgba(244,63,94,0.1)]` }><StrikeChip strike={ maxStrike } type="resistance" /></td>
            ) }

            { isVisible( COLUMN_IDS.MIN_OI_DIFF ) && (
                <td className={ `${tdClass} text-right` }><NumberCell value={ minDiff } decimals={ 2 } suffix="%" className="text-emerald-600 font-bold" /></td>
            ) }

            { isVisible( COLUMN_IDS.MIN_STRIKE ) && (
                <td className={ `${tdClass} text-center shadow-[inset_1px_0_0_0_rgba(16,185,129,0.1)]` }><StrikeChip strike={ minStrike } type="support" /></td>
            ) }

            { isVisible( COLUMN_IDS.CE_COUNT ) && (
                <td className={ `${tdClass} text-center` }><NumberCell value={ ceCount } decimals={ 0 } showSign={ true } /></td>
            ) }

            { isVisible( COLUMN_IDS.PE_COUNT ) && (
                <td className={ `${tdClass} text-center` }><NumberCell value={ peCount } decimals={ 0 } showSign={ true } /></td>
            ) }

            { isVisible( COLUMN_IDS.FINAL_COUNT ) && (
                <td className={ `${tdClass} text-center` }><NumberCell value={ finalCount } decimals={ 0 } showSign={ true } /></td>
            ) }

            { isVisible( COLUMN_IDS.VARIANCE ) && (
                <td className={ `${tdClass} text-center` }>
                    <VarianceChip
                        value={ variance }
                        previousValue={ prevItem ? ( prevItem.variance ?? prevItem.finalVariance ) : undefined }
                    />
                </td>
            ) }

            { isVisible( COLUMN_IDS.SHIFT ) && (
                <td className={ `${tdClass} text-center bg-indigo-50/20 dark:bg-indigo-900/10` }>
                    <ShiftChip
                        value={ shift }
                        previousValue={ prevItem ? prevItem.shift : undefined }
                    />
                </td>
            ) }

            { isVisible( COLUMN_IDS.SHIFT_OLD ) && (
                <td className={ `${tdClass} text-center` }>
                    <ShiftChip
                        value={ shiftOld }
                        previousValue={ prevItem ? ( prevItem.shiftOld ?? prevItem.shift_old ) : undefined }
                    />
                </td>
            ) }

            { isVisible( 'signal' ) && (
                <td className={ `${tdClass} text-center` }>
                    { ( () =>
                    {
                        if ( variance > 15 && finalCount > 1 )
                            return (
                                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded border border-emerald-100 dark:border-emerald-800 uppercase tracking-wider">
                                    BUY
                                </span>
                            )
                        if ( variance < -15 && finalCount < -1 )
                            return (
                                <span className="text-[10px] font-black text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 px-2 py-1 rounded border border-rose-100 dark:border-rose-800 uppercase tracking-wider">
                                    SELL
                                </span>
                            )
                        return (
                            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                WAIT
                            </span>
                        )
                    } )() }
                </td>
            ) }

            { isVisible( COLUMN_IDS.ATM ) && (
                <td className={ `${tdClass} text-center` }>
                    <div className="flex flex-col items-center">
                        <StrikeChip strike={ atmStrike } type="atm" />
                        { spotPrice > 0 && <span className="text-[9px] text-slate-400 mt-0.5">{ spotPrice.toFixed( 2 ) }</span> }
                    </div>
                </td>
            ) }

            <td className={ `${tdClass} text-center pr-4 text-slate-300 group-hover:text-slate-400 transition-colors` }>
                <ChevronRight className="h-4 w-4 ml-auto" />
            </td>
        </>
    )
} )

const VarianceHistoryTableContent: React.FC<VarianceHistoryTableProps> = ( {
    data,
    symbol,
    // Unused but kept for interface compatibility
    rowsPerPage: controlledRowsPerPage,
    onRowsPerPageChange,
} ) =>
{
    const handleExport = useExportHandler( data, symbol )
    const [ visibleLimit, setVisibleLimit ] = useState( 10 )
    const [ internalRowsPerPage, setInternalRowsPerPage ] = useState( 10 )

    useEffect( () =>
    {
        const saved = localStorage.getItem( CONFIG.UI.STORAGE_KEYS.VARIANCE_HISTORY_ROWS )
        if ( saved )
        {
            const parsed = parseInt( saved, 10 )
            setInternalRowsPerPage( parsed )
            setVisibleLimit( parsed )
        }
    }, [] )

    const rowsPerPage = controlledRowsPerPage ?? internalRowsPerPage

    // Sync visibleLimit when rowsPerPage changes
    useEffect( () =>
    {
        if ( rowsPerPage ) setVisibleLimit( rowsPerPage )
    }, [ rowsPerPage ] )

    // Use centralized column visibility hook with lazy initialization
    const defaultColumns: ColumnConfig[] = VARIANCE_COLUMNS.map( ( c ) => ( { id: c.id, label: c.label, visible: true } ) )
    const [ columns, toggleColumn, isVisible ] = useColumnVisibility(
        CONFIG.UI.STORAGE_KEYS.VARIANCE_HISTORY_COLUMNS,
        defaultColumns,
    )

    // Save rows per page
    useEffect( () =>
    {
        if ( rowsPerPage && typeof window !== 'undefined' )
        {
            localStorage.setItem( CONFIG.UI.STORAGE_KEYS.VARIANCE_HISTORY_ROWS, rowsPerPage.toString() )
        }
    }, [ rowsPerPage ] )

    // Sorting Logic
    const { sortedData: items, requestSort } = useSortedData( data || [], FIELD_KEYS.TIMESTAMP[ 0 ], 'desc' )

    const totalItems = items.length

    const deferredData = React.useDeferredValue( items )

    const handleRowsPerPageChange = ( e: React.ChangeEvent<HTMLSelectElement> ) =>
    {
        const newRows = Number( e.target.value )
        if ( onRowsPerPageChange )
        {
            onRowsPerPageChange( newRows )
        } else
        {
            setInternalRowsPerPage( newRows )
        }
    }

    const tdClass = 'p-3 align-middle border-b border-slate-100/50 dark:border-slate-800/50 whitespace-nowrap text-sm'
    const headerClass =
        'p-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/50 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors select-none'

    return (
        <TableWrapper className="bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800 rounded-lg transition-colors duration-300">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex flex-wrap justify-between items-center gap-4 bg-white dark:bg-slate-900 rounded-t-lg transition-colors duration-300">
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-rose-50 dark:bg-rose-900/20 rounded-lg text-rose-600 dark:text-rose-400">
                        <Activity size={ 18 } />
                    </div>
                    <h3 className="font-bold text-slate-700 dark:text-slate-200 text-sm md:text-base">
                        Variance History
                    </h3>
                    <span className="text-xs text-slate-400 dark:text-slate-500 font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                        { totalItems }
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={ rowsPerPage }
                        onChange={ handleRowsPerPageChange }
                        className="h-9 pl-3 pr-8 text-xs font-medium text-foreground bg-background/50 backdrop-blur-sm border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer shadow-sm hover:border-muted-foreground/40 transition-colors"
                        title="Initial rows to load (Chunk size)"
                    >
                        { [ 10, 15, 25, 50, 100 ].map( ( n ) => (
                            <option key={ n } value={ n }>{ n } rows</option>
                        ) ) }
                    </select>
                    <ExcelDownloadButton onExport={ handleExport } title="Download Variance History as Excel" />
                    <ColumnSelector columns={ columns } onChange={ toggleColumn } />
                </div>
            </div>

            <div className="relative overflow-hidden" style={ { height: Math.min( deferredData.length * 48 + 60, 520 ) } }>
                <TableVirtuoso
                    data={ deferredData }
                    overscan={ 8 }
                    style={ { height: '100%' } }
                    components={ {
                        Table: React.forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement>>( ( props, ref ) => (
                            <table ref={ ref } { ...props } className="w-full text-left border-collapse" />
                        ) ),
                        TableHead: React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>( ( props, ref ) => (
                            <thead ref={ ref } { ...props } className="sticky top-0 z-10 bg-white dark:bg-slate-900 shadow-sm transition-colors duration-300" />
                        ) ),
                        TableRow: ( props ) => (
                            <tr { ...props } className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" />
                        ),
                    } }
                    fixedHeaderContent={ () => (
                        <tr>
                            <th className={ `${headerClass} text-left pl-4` }>Date Time</th>
                            { isVisible( COLUMN_IDS.MAX_OI_DIFF ) && (
                                <th className={ `${headerClass} text-right` } onClick={ () => requestSort( FIELD_KEYS.MAX_OI_DIFF[ 0 ] ) }>Max Diff</th>
                            ) }
                            { isVisible( COLUMN_IDS.MAX_STRIKE ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.MAX_STRIKE[ 0 ] ) }>Max Strike</th>
                            ) }
                            { isVisible( COLUMN_IDS.MIN_OI_DIFF ) && (
                                <th className={ `${headerClass} text-right` } onClick={ () => requestSort( FIELD_KEYS.MIN_OI_DIFF[ 0 ] ) }>Min Diff</th>
                            ) }
                            { isVisible( COLUMN_IDS.MIN_STRIKE ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.MIN_STRIKE[ 0 ] ) }>Min Strike</th>
                            ) }
                            { isVisible( COLUMN_IDS.CE_COUNT ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.CE_COUNT[ 0 ] ) }>CE Count</th>
                            ) }
                            { isVisible( COLUMN_IDS.PE_COUNT ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.PE_COUNT[ 0 ] ) }>PE Count</th>
                            ) }
                            { isVisible( COLUMN_IDS.FINAL_COUNT ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.FINAL_COUNT[ 0 ] ) }>Total Count</th>
                            ) }
                            { isVisible( COLUMN_IDS.VARIANCE ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.VARIANCE[ 0 ] ) }>Variance</th>
                            ) }
                            { isVisible( COLUMN_IDS.SHIFT ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.SHIFT[ 0 ] ) }>Shift</th>
                            ) }
                            { isVisible( COLUMN_IDS.SHIFT_OLD ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.SHIFT_OLD[ 0 ] ) }>Shift Old</th>
                            ) }
                            { isVisible( 'signal' ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( 'signal' ) }>Signal</th>
                            ) }
                            { isVisible( COLUMN_IDS.ATM ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.ATM[ 0 ] ) }>ATM</th>
                            ) }
                            <th className={ `${headerClass} w-10` }></th>
                        </tr>
                    ) }
                    itemContent={ ( index, row ) => (
                        <VarianceRow
                            row={ row }
                            idx={ index }
                            items={ items }
                            isVisible={ isVisible }
                        />
                    ) }
                />
            </div>
        </TableWrapper>
    )
}

export const VarianceHistoryTable = ( props: VarianceHistoryTableProps ) => (
    <WidgetErrorBoundary fallbackDescription="Variance History">
        <VarianceHistoryTableContent { ...props } />
    </WidgetErrorBoundary>
)

export default VarianceHistoryTable
